import client from "libs/client";
import React, { type KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import type { SingleValue } from "react-select";
import { useToast } from "ui-kit";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { formatDate, formatDateTime } from "features/discover/functions";
import Loader from "components/Loader";
import Edit from "ui-kit/assets/edit/edit.svg";

interface ContentCellProps {
	id?: number | string;
	title?: string;
	type: string | "string" | "phone" | "number" | "boolean" | "list" | "date" | "datetime" | "empty",
	formatter?: (value: string | number | boolean | null | undefined) => string | ReactNode;
	transformer?: (value: string | number | boolean | null | undefined) => string | number | boolean | null;
	value?: string | number | boolean | null | undefined;
	field?: string;
	mutation?: any;
	invalidationKey: string | string[];
	isSelectLoading?: boolean;
	options?: {
		label: string;
		value: string | number;
	}[];
	externalEditControl?: boolean;
	isEdit?: boolean;
	onEditChange?: (edit: boolean) => void;
	showEditIcon?: boolean;
	onEditIconClick?: (e: React.MouseEvent) => void;
}

export function ContentCell({ id, field, type, formatter, transformer, value, title, mutation, invalidationKey, isSelectLoading, options = [], externalEditControl = false, isEdit: externalIsEdit, onEditChange, showEditIcon = false, onEditIconClick }: ContentCellProps) {
	const [internalEdit, setInternalEdit] = useState(false);
	const edit = externalEditControl ? (externalIsEdit ?? false) : internalEdit;
	const setEdit = externalEditControl ? (onEditChange || (() => { })) : setInternalEdit;
	const [saving, setSaving] = useState(false);
	const [inputValue, setInputValue] = useState(value);

	const { addSuccessToast, addErrorToast } = useToast();
	const inputRef = useRef<HTMLInputElement>(null);

	const getInitSelectValue = () => {
		return options?.find((item) => item.value === value);
	}

	const [selectValue, setSelectValue] = useState(type === "list" ? getInitSelectValue() : undefined)

	if (type === "empty") {
		return <div className="detailCell"></div>;
	}

	const onSubmit = () => {
		let newValue = inputValue;

		if (type === "date") {
			newValue = newValue === "" ? null : newValue;
		} else if (type === "list") {
			newValue = selectValue?.value;
		} else if (type === "boolean") {
			newValue = !!inputValue;
		}

		if (field) {
			if (!mutation) {
				console.log("Mutation is not defined");
			}

			setSaving(true);
			mutation.mutate({
				id: id,
				body: {
					[field]: transformer ? transformer(newValue) : newValue
				}
			}, {
				onSuccess: () => {
					setEdit(false)
					client.queryClient.invalidateQueries({ predicate: (query) => {
						let result = false;
						if (typeof invalidationKey === "string") {
							result = query.queryKey.includes(invalidationKey);
						} else {
							invalidationKey.forEach((key) => {
								if (query.queryKey.includes(key)) {
									result = true;
								}
							})
						}
						return result;
					}, })
					addSuccessToast("Field updated");
					setSaving(false);
				},
				onError: (error: unknown) => {
					addErrorToast(error instanceof Error ? error.message : 'Update failed');
					setSaving(false);
				}
			});
		}
	}

	useEffect(() => {
		if (edit && inputRef.current) {
			inputRef.current.focus();
		}
	}, [edit]);

	useEffect(() => {
		setSelectValue(getInitSelectValue());
	}, [isSelectLoading]);

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			onSubmit()
		}
		if (e.key === "Escape") {
			setEdit(false)
			setInputValue(null)
		}
	}

	const onInputChange = (value: string | number | boolean | null) => {
		switch (type) {
			case "boolean":
				setInputValue(!!value)
				break;
			case "phone":
				setInputValue((String(value)).replace(/[^0-9+]/g, ''));
				break;
			case "number":
				setInputValue(parseInt(String(value)));
				break;
			default:
				setInputValue(value);
		}
	}

	const onSelectChange = (value: SingleValue<{ label: string; value: string | number; }>) => {
		setSelectValue((value ? value : undefined))
	}

	const handleCancel = () => {
		if (options) {
			setSelectValue(getInitSelectValue());
		} else {
			setInputValue(value);
		}
		setEdit(false);
	};

	const renderEditableContent = () => {
		if (!edit) {
			return null;
		}

		if (type === "boolean") {
			if (options.length > 0) {
				return <>
					{options.map((option, optionIndex) => <label htmlFor={`${option.value}-${field}`} className="cellCheckboxLabel" key={`option-${optionIndex}`}>
						<input
							id={`${option.value}-${field}`}
							ref={inputRef}
							className="cellInput"
							type="radio"
							onKeyDown={onKeyDown}
							checked={option.value === inputValue}
							onChange={(e) => setInputValue(option.value)}
						/>
						{option.label}
					</label>)}
				</>;
			}

			return (
				<label htmlFor={field} className="cellCheckboxLabel">
					<input
						id={field}
						ref={inputRef}
						className="cellInput"
						type="checkbox"
						onKeyDown={onKeyDown}
						checked={!!inputValue}
						onChange={(e) => setInputValue(e.target.checked)}
					/>
					{title}
				</label>
			);
		}

		if ((type === "string" || type === "phone")) {
			return (
				<input
					ref={inputRef}
					className="cellInput"
					type="text"
					onKeyDown={onKeyDown}
					value={inputValue?.toString()}
					onChange={(e) => onInputChange(e.target.value)}
				/>
			);
		}

		if (type === "number") {
			return (
				<input
					ref={inputRef}
					className="cellInput"
					type="number"
					onKeyDown={onKeyDown}
					value={inputValue?.toString()}
					min={0}
					onChange={(e) => onInputChange(e.target.value)}
				/>
			);
		}

		if (type === "date") {
			return (
				<input
					ref={inputRef}
					className="cellInput"
					type="date"
					onKeyDown={onKeyDown}
					value={inputValue ? (inputValue as string).substring(0, 10) : ''}
					onChange={(e) => onInputChange(e.target.value)}
				/>
			);
		}
		if (type === "datetime") {
			return (
				<input
					ref={inputRef}
					className="cellInput"
					type="datetime-local"
					onKeyDown={onKeyDown}
					value={inputValue as string}
					onChange={(e) => onInputChange(e.target.value)}
				/>
			);
		}

		if (type === "list") {
			return (
				<SelectComponent
					isCleareable
					value={selectValue}
					handleChange={onSelectChange}
					isLoading={!!isSelectLoading}
					isDisabled={!!isSelectLoading}
					items={options}
				/>
			);
		}

		return null;
	};

	const renderContent = () => {
		if (edit) {
			return null;
		}

		if (formatter) {
			return formatter(value) || '-';
		}

		if (type === "boolean") {
			return (
				<div className={value ? "true" : "false"}>
					{value ? 'Yes' : 'No'}
				</div>
			);
		}

		if (type === "list") {
			return getInitSelectValue()?.label ?? "-";
		}

		if (type === "date") {
			return formatDate(value as string | null);
		}

		if (type === "datetime") {
			return formatDateTime(value as string | null);
		}

		return value || "-";
	};

	const shouldShowEditIcon = showEditIcon && !edit;

	return (
		<div
			className={`detailCell ${field && !edit ? 'detailCell--editable' : ''} ${field && edit ? 'detailCell--editing' : ''} ${saving ? 'detailCell--saving' : ''}`}
			onClick={externalEditControl ? undefined : () => field && !edit ? setEdit(true) : undefined}
		>
			<div className="cellTitle">
				{title}
			</div>
			<div className={`cellValue ${shouldShowEditIcon ? 'cellValue--withEditIcon' : ''}`}>
				<div className="cellContent">
					{isSelectLoading ? <Loader /> : renderContent()}
					{renderEditableContent()}
				</div>
				{shouldShowEditIcon && onEditIconClick && (
					<>
						<div className="cellValue__divider"></div>
						<div
							className="cellValue__editIcon"
							onClick={(e) => {
								e.stopPropagation();
								onEditIconClick(e);
							}}
							style={{
								backgroundColor: 'white',
								borderLeft: '1px solid #000',
							}}
						>
							<img
								src={Edit}
								alt="edit"
								style={{
									width: '16px',
									height: '16px',
									display: 'block',
								}}
							/>
						</div>
					</>
				)}
				{edit && (
					<div className="buttons">
						<button className="cancelButton" type="button" onClick={handleCancel}>Cancel</button>
						<button className="submitButton" type="button" onClick={onSubmit}>Submit</button>
					</div>
				)}
			</div>
		</div>
	);
}
